import { type AnyRoute, createRouter } from '@tanstack/react-router'

import { mainTree, platformTree } from '@/app/router'
import type { System } from '@/lib/auth-store'
import { slugProblem } from '@/lib/slug'

export const SEARCH_REDIRECT = 'redirect'

export interface LoginSearch {
  /** Where to go after login (PRD login routing); undefined when unsafe. */
  [SEARCH_REDIRECT]?: string | undefined
}

// Platform pages live at the root of their own host (platform.<main host>,
// 2026-09-01), so the two systems never share a path space.

export function loginPathFor(system: System) {
  return system === 'platform' ? '/login' : '/app/login'
}

/** Where a logged-in user lands by default: the account area on the main host, the restaurant list on the platform. */
export function homePathFor(system: System): '/restaurants' | '/app/profile' {
  return system === 'platform' ? '/restaurants' : '/app/profile'
}

export function forgotPasswordPathFor(system: System) {
  return system === 'platform' ? '/forgot-password' : '/app/forgot-password'
}

export function profilePathFor(system: System): '/profile' | '/app/profile' {
  return system === 'platform' ? '/profile' : '/app/profile'
}

/** The notifications page (H3): one per host, like the profile. */
export function notificationsPathFor(system: System): '/notifications' | '/app/notifications' {
  return system === 'platform' ? '/notifications' : '/app/notifications'
}

/** Restaurant-only registration (PRD: platform accounts are admin-created). */
export const REGISTER_PATH = '/app/register'

/** The customer's order history (H6); restaurant accounts only. */
export const MY_ORDERS_PATH = '/app/my-orders'

/** The back-office entry (E6): the user's restaurants and their roles. */
export const MY_RESTAURANTS_PATH = '/app/restaurants'

/** Pages the redirect must never target — landing there again would loop. */
const AUTH_ENTRY_PATHS: Record<System, ReadonlySet<string>> = {
  restaurant: new Set([loginPathFor('restaurant'), forgotPasswordPathFor('restaurant'), REGISTER_PATH]),
  platform: new Set([loginPathFor('platform'), forgotPasswordPathFor('platform')]),
}

/** One probe per host's tree, built on first use; `isServer` keeps it off the DOM and history. */
const probes: Partial<Record<System, ReturnType<typeof createRouter>>> = {}

function probeFor(system: System) {
  const routeTree: AnyRoute = system === 'platform' ? platformTree : mainTree
  return (probes[system] ??= createRouter({ routeTree, isServer: true }))
}

/** Exactly served by the host's tree (`**` = a fuzzy remainder), with well-formed slug params. */
function routesOn(system: System, pathname: string): boolean {
  const [, params, route] = probeFor(system).getMatchedRoutes(pathname)
  if (!route || '**' in params) return false
  const { restaurantSlug, branchSlug } = params as { restaurantSlug?: string; branchSlug?: string }
  return [restaurantSlug, branchSlug].every((slug) => slug === undefined || slugProblem(slug) === undefined)
}

/** Same-origin paths the host's route tree serves, never an auth entry page (no login→login loop). */
export function safeReturnPath(system: System, raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//')) return undefined
  if (/[\\\s]/.test(raw) || raw.includes('/\\')) return undefined
  const pathname = (raw.split(/[?#]/, 1)[0] ?? '').replace(/\/+$/, '') || '/'
  if (!routesOn(system, pathname)) return undefined
  return AUTH_ENTRY_PATHS[system].has(pathname) ? undefined : raw
}

/** Search params for a login link that returns to `location` afterward. */
export function loginSearchFor(
  system: System,
  location: { pathname: string; searchStr?: string },
): LoginSearch {
  const target = safeReturnPath(system, `${location.pathname}${location.searchStr ?? ''}`)
  return target ? { [SEARCH_REDIRECT]: target } : {}
}

/**
 * Validates the login page's `redirect`. The router merges validated search
 * over the raw one, so an unsafe value must be overwritten, not omitted.
 */
export function parseLoginSearchFor(system: System) {
  return (search: Record<string, unknown>): LoginSearch => ({
    [SEARCH_REDIRECT]: safeReturnPath(system, search[SEARCH_REDIRECT]),
  })
}

/** Where a successful login (or verify / reset) lands: the return path, else home. */
export function afterLoginPath(search: LoginSearch, system: System): string {
  return safeReturnPath(system, search[SEARCH_REDIRECT]) ?? homePathFor(system)
}

/** The two-step pages (register, forgot password) keep their step in the URL. */
export interface RegisterSearch extends LoginSearch {
  step?: 'verify' | undefined
}

export function parseRegisterSearchFor(system: System) {
  return (search: Record<string, unknown>): RegisterSearch => ({
    ...parseLoginSearchFor(system)(search),
    step: search.step === 'verify' ? 'verify' : undefined,
  })
}
